import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import { Modal, Upload, message } from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import { AppButton } from "components/antd-ui";
import { useCallback, useEffect, useState } from "react";
import "./ImportExcelModal.scss";

export type ImportExcelResult = {
  status: "ok" | "error";
  msg: string;
  rowNum: number;
  action?: string;
};

type ImportExcelModalProps = {
  open: boolean;
  title: string;
  sampleUrl: string;
  sampleFileName: string;
  onClose: () => void;
  onSuccess: () => void;
  importFile: (file: File) => Promise<ImportExcelResult[]>;
};

const allowExtensions = ["xlsx", "xls"];

export const ImportExcelModal = ({
  open,
  title,
  sampleUrl,
  sampleFileName,
  onClose,
  onSuccess,
  importFile,
}: ImportExcelModalProps) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<ImportExcelResult[] | null>(null);

  useEffect(() => {
    if (!open) {
      setFileList([]);
      setResults(null);
      setLoading(false);
    }
  }, [open]);

  const handleDownloadSample = useCallback(() => {
    const link = document.createElement("a");
    link.href = sampleUrl;
    link.download = sampleFileName;
    link.click();
  }, [sampleFileName, sampleUrl]);

  const handleSubmit = useCallback(async () => {
    const file = fileList[0]?.originFileObj;
    if (!file) {
      message.warning("Chọn file Excel");
      return;
    }

    setLoading(true);
    try {
      const nextResults = await importFile(file);
      const errorCount = nextResults.filter((item) => item.status === "error").length;
      const okCount = nextResults.length - errorCount;
      setResults(nextResults);

      if (!nextResults.length) {
        message.warning("File không có dòng dữ liệu");
        return;
      }

      if (errorCount === 0) {
        message.success(`Import thành công ${okCount} dòng`);
        onSuccess();
        onClose();
        return;
      }

      if (okCount > 0) {
        onSuccess();
      }
      message.warning(`Thành công ${okCount} dòng, lỗi ${errorCount} dòng`);
    } finally {
      setLoading(false);
    }
  }, [fileList, importFile, onClose, onSuccess]);

  const errors = (results || []).filter((item) => item.status === "error");

  return (
    <Modal
      className="importExcelModal"
      open={open}
      title={title}
      onCancel={onClose}
      width={560}
      destroyOnClose
      footer={
        <div className="importExcelModal__footer">
          <AppButton onClick={onClose}>Đóng</AppButton>
          <AppButton tone="primary" loading={loading} onClick={handleSubmit}>
            Import
          </AppButton>
        </div>
      }
    >
      <div className="importExcelModal__sample">
        <AppButton icon={<DownloadOutlined />} onClick={handleDownloadSample}>
          Tải file mẫu
        </AppButton>
      </div>
      <Upload
        accept=".xlsx,.xls"
        maxCount={1}
        fileList={fileList}
        beforeUpload={(file) => {
          const extension = file.name.split(".").pop()?.toLowerCase() || "";
          if (!allowExtensions.includes(extension)) {
            message.error("Chỉ chấp nhận file .xlsx, .xls");
            return Upload.LIST_IGNORE;
          }
          setResults(null);
          return false;
        }}
        onChange={({ fileList: next }) => {
          setFileList(next.slice(-1));
        }}
        onRemove={() => {
          setResults(null);
        }}
      >
        <AppButton icon={<UploadOutlined />}>Chọn file Excel</AppButton>
      </Upload>
      <p className="importExcelModal__hint">Chấp nhận file .xlsx, .xls.</p>
      {!!errors.length && (
        <div className="importExcelModal__errors">
          {errors.map((item) => (
            <div key={`${item.rowNum}-${item.msg}`} className="importExcelModal__error">
              Dòng {item.rowNum}: {item.msg}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};
